import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiX, FiMaximize, FiImage, FiMove } from 'react-icons/fi';

const ImageSettingsPopup = ({ onClose, onUpdate, currentStyles = {} }) => {
  const [styles, setStyles] = useState({
    objectFit: currentStyles.objectFit || 'cover',
    fullScreen: currentStyles.fullScreen || false,
    width: currentStyles.width || 100,
    height: currentStyles.height || 'auto',
    maxWidth: currentStyles.maxWidth || '100%',
  });

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-teal-100 rounded-lg text-teal-600">
              <SafeIcon icon={FiImage} className="w-4 h-4" />
            </div>
            <h3 className="font-bold text-gray-800">Image Settings</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Image Fit</label>
            <div className="flex p-1 bg-gray-100 rounded-xl">
              {['cover', 'contain', '100% 100%'].map(fit => (
                <button
                  key={fit}
                  onClick={() => setStyles({ ...styles, objectFit: fit })}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold capitalize transition-all ${styles.objectFit === fit ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {fit === 'cover' ? 'Fill' : fit === 'contain' ? 'Fit' : 'Stretch'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Image Width (%)</label>
            <div className="relative">
              <input
                type="range"
                min="10"
                max="100"
                step="1"
                value={styles.width}
                onChange={(e) => setStyles({ ...styles, width: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
              />
              <div className="flex justify-between mt-2">
                <span className="text-xs text-gray-500">10%</span>
                <span className="text-sm font-bold text-teal-600">{styles.width}%</span>
                <span className="text-xs text-gray-500">100%</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Max Width (px)</label>
            <input
              type="text"
              value={styles.maxWidth}
              onChange={(e) => setStyles({ ...styles, maxWidth: e.target.value })}
              placeholder="e.g., 800px or 100%"
              className="w-full p-2.5 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 text-sm text-gray-800 transition-all"
            />
            <p className="text-xs text-gray-400 mt-1.5">Use pixels (800px) or percentage (100%)</p>
          </div>

          <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <SafeIcon icon={FiMaximize} className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Full Screen Height</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-0.5">Stretch image to fill the screen</p>
                </div>
              </div>
              <button onClick={() => setStyles({...styles, fullScreen: !styles.fullScreen})} className={`relative w-11 h-6 rounded-full transition-colors ${styles.fullScreen ? 'bg-teal-500' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${styles.fullScreen ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
            <SafeIcon icon={FiMove} className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700">
              <p className="font-bold mb-1">Tip: Drag to Resize</p>
              <p>Click on the image in the canvas and drag the corner handles to resize it visually.</p>
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 flex space-x-3 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-xl transition-colors">
            Discard
          </button>
          <button onClick={() => { onUpdate(styles); onClose(); }} className="flex-[2] py-3 bg-teal-500 text-white font-bold rounded-xl shadow-lg shadow-teal-100 hover:bg-teal-600 transition-all">
            Apply Settings
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ImageSettingsPopup;